import { IonInfiniteScroll } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'

import {
  QUERY_KEY_PORTAL_SEARCH_UNITS,
  QUERY_KEY_PORTAL_SEARCH_UNITS_COUNT,
  usePortalSearchUnitsCount,
  usePortalSearchUnitsInfinite
} from '@apps-common/apis/portal/unit'
import { PortalUnitsHeader, UnitListCardSmSkeleton } from '@apps-common/apps/portal/pages/units/components'
import { InfiniteLoader, NoResultsCard, PullRefresh, RootHeaderButtons } from '@apps-common/components/common'
import { LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { EndOfResults } from '@apps-common/components/modals/unit-history'
import { PageClearFilterButton, PageResultsCount } from '@apps-common/components/page'
import { NO_RESULTS_DEFAULT_FILTER_MESSAGE } from '@apps-common/config/app-messages'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'
import { usePortalUnitsFilterStore } from '@apps-common/stores'
import { IPortalUnitSearchModel } from '@apps-common/types/portal/unit'
import { PortalUnitListCard } from './PortalUnitListCard'

export const PortalUnitsInfiniteView = () => {
  const { navigateForward } = useNavigation()
  const filter = usePortalUnitsFilterStore()

  const {
    isLoading: countLoading,
    data: countData,
    refetch: countRefetch
  } = usePortalSearchUnitsCount({
    model: filter
  })

  const {
    isLoading: unitsLoading,
    isFetching: unitsFetching,
    isRefetching: unitsRefetching,
    isError: unitsError,
    data: units,
    refetch: unitsRefetch,
    fetchNextPage: unitsFetchNext,
    hasNextPage: unitsHasNextPage
  } = usePortalSearchUnitsInfinite({
    enabled: true,
    model: filter
  })

  const reducedData = units?.pages.reduce((acc: IPortalUnitSearchModel[], curr: IPortalUnitSearchModel[]) => [...acc, ...curr], []) || []
  const loading = unitsLoading || unitsError

  return (
    <>
      <PortalUnitsHeader
        icons={(<RootHeaderButtons />) as any}
        leftComponent={<PageResultsCount loading={countLoading} count={countData} extraText={filter.status} />}
        rightComponent={<PageClearFilterButton filter={filter} filterKeyWordsFunc={filter.getFilteredByKeywords} callback={filter.clear} />}
        loading={countLoading}
        navigateForward={navigateForward}
        searchCount={countData?.resultCount || -1}
      />
      <LayoutContainer>
        <LayoutPadding topPadding='relative pt-[40px] md:pt-[70px]'>
          <PullRefresh
            loading={unitsRefetching}
            callback={() => {
              queryClient.removeQueries({ queryKey: QUERY_KEY_PORTAL_SEARCH_UNITS_COUNT(filter) })
              queryClient.removeQueries({ queryKey: QUERY_KEY_PORTAL_SEARCH_UNITS(filter) })
              countRefetch()
              unitsRefetch()
            }}
          />
          <>
            <div className='grid sm:grid-cols-2 sm:max-w-[1200px] gap-[15px]'>
              <AnimatePresence>
                {loading &&
                  Array.from(Array(25).keys()).map((k, i) => (
                    <motion.div key={`motion-portal-unit-list-card-skeleton-${i}`} {...motionFadeIn(0.2)}>
                      <UnitListCardSmSkeleton
                        key={`portal-unit-list-skeleton-${k}`}
                        index={i}
                        bgClasses='bg-skeleton border border-skeleton-border min-w-full sm:min-w-[200px] lg:min-w-[250px]'
                        textClasses=''
                        iconClasses=''
                        bgSecondaryClasses='bg-skeleton-hi'
                        textSecondaryClasses=''
                        bgTertiaryClasses='bg-skeleton-hi'
                      />
                    </motion.div>
                  ))}
                {!loading &&
                  reducedData.map((u: IPortalUnitSearchModel) => (
                    <motion.div key={`motion-portal-unit-list-card-${u?.unitId}`} {...motionFadeIn(0.2)} className='w-full'>
                      <PortalUnitListCard
                        key={`portal-unit-list-${u.unitId}`}
                        bgClasses='bg-surface border border-border min-w-full lg:min-w-[250px]'
                        textClasses='text-text'
                        iconClasses='text-text'
                        bgSecondaryClasses='bg-surface-2'
                        textSecondaryClasses='text-text'
                        bgTertiaryClasses='bg-surface'
                        unit={u}
                        navigateForward={navigateForward}
                      />
                    </motion.div>
                  ))}
                {!loading && reducedData.length === 0 && <NoResultsCard>{NO_RESULTS_DEFAULT_FILTER_MESSAGE}</NoResultsCard>}
                <EndOfResults key='motion-portal-unit-list-end-of-results' show={reducedData.length > 0 && !unitsHasNextPage} />
              </AnimatePresence>
            </div>

            <IonInfiniteScroll
              onIonInfinite={async (ev) => {
                await unitsFetchNext()
                ev.target.complete()
              }}
            >
              <InfiniteLoader key='units-management-infinite-loader' loading={unitsFetching} />
            </IonInfiniteScroll>
          </>
        </LayoutPadding>
      </LayoutContainer>
    </>
  )
}
